/**
 * SDF distortion CPU implementation - adjusts vertex positions in UV space according to a stack of distortion filters.
 * @module
 */
import type { SdfDistortion } from "../designMaterialTypes";
import { hash21, hash32, mixVec } from "./glslEmulationUtil";

export const DISTORTED_SDF_MIN_VERTEX_INTERVAL = 2; // world units (inches)

export function distortionPass(x: number, y: number, cellId: number, distortion: SdfDistortion): number[] {
  let gridCoordsX = x / distortion.periodX;
  let gridCoordsY = y / distortion.periodY;
  const [randOffsetX, randOffsetY] = hash21((cellId % 255) / 255);
  gridCoordsX += randOffsetX * distortion.randomness;
  gridCoordsY += randOffsetY * distortion.randomness;
  const cellIndexX = Math.floor(gridCoordsX);
  const cellIndexY = Math.floor(gridCoordsY);
  const randX0Y0 = hash32(cellIndexX, cellIndexY);
  const randX0Y1 = hash32(cellIndexX, cellIndexY + 1);
  const randX1Y0 = hash32(cellIndexX + 1, cellIndexY);
  const randX1Y1 = hash32(cellIndexX + 1, cellIndexY + 1);
  const interpolationTX = gridCoordsX - cellIndexX;
  const interpolationTY = gridCoordsY - cellIndexY;
  const interpolatedOffset = mixVec(
    mixVec(randX0Y0, randX0Y1, interpolationTY),
    mixVec(randX1Y0, randX1Y1, interpolationTY),
    interpolationTX,
  );
  return [
    (interpolatedOffset[0] - 0.5) * distortion.strengthX,
    (interpolatedOffset[1] - 0.5) * distortion.strengthY,
    (interpolatedOffset[2] - 1.0) * distortion.strengthZ,
  ];
}

export function distortSDFVertex(
  x: number,
  y: number,
  z: number,
  cellId: number,
  distortions: SdfDistortion[],
): number[] {
  const distortionOffset = [0, 0, 0];
  for (const distortion of distortions) {
    if (distortion.enabled) {
      const passOffset = distortionPass(x, y, cellId, distortion);
      distortionOffset[0] += passOffset[0];
      distortionOffset[1] += passOffset[1];
      distortionOffset[2] += passOffset[2];
    }
  }
  return [x + distortionOffset[2], y + distortionOffset[1], z + distortionOffset[2]];
}
